import {
	createContext,
	useContext,
	useEffect,
	useReducer,
	useState,
} from 'react';

const QuizContext = createContext();

const SECS_PER_QUESTION = 30;

function shuffleArray(array) {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
}

const initialState = {
	questions: [],

	// 'loading', 'error', 'ready', 'active', 'finished'
	status: 'loading',
	index: 0,
	answer: null,
	points: 0,
	highscore: 0,
	secondsRemaining: null,
};

function reducer(state, action) {
	switch (action.type) {
		case 'dataReceived':
			return {
				...state,
				questions: action.payload,
				status: 'ready',
			};

		case 'dataFailed':
			return {
				...state,
				status: 'error',
			};
		case 'start':
			return {
				...state,
				status: 'active',
				secondsRemaining: state.questions.length * SECS_PER_QUESTION,
			};

		case 'newAnswer':
			const question = state.questions[state.index];
			return {
				...state,
				answer: action.payload,
				points:
					action.payload === question.correctOption
						? state.points + question.points
						: state.points,
			};
		case 'nextQuestion':
			return {
				...state,
				index: state.index + 1,
				answer: null,
			};

		case 'finish':
			return {
				...state,
				status: 'finished',
				highscore:
					state.points > state.highscore ? state.points : state.highscore,
				index: 0,
				answer: null,
			};

		case 'restart':
			const shuffledQuestions = [...state.questions];
			shuffleArray(shuffledQuestions);
			const newRandomQuestions = shuffledQuestions.slice(0, 15);
			return {
				...state,
				status: 'ready',
				index: 0,
				answer: null,
				points: 0,
				questions: newRandomQuestions,
			};

		// return { ...initialState, questions: state.questions, status: 'ready' };

		case 'tick':
			return {
				...state,
				secondsRemaining: state.secondsRemaining - 1,
				status: state.secondsRemaining <= 0 ? 'finished' : state.status,
			};
		default:
			throw new Error(`Unhandled action type: ${action.type}`);
	}
}

function QuizProvider({ children }) {
	const [
		{ questions, status, index, answer, points, highscore, secondsRemaining },
		dispatch,
	] = useReducer(reducer, initialState);

	const [totalQuestions, setTotalQuestions] = useState(0);

	const maxPossiblePoints = questions.reduce(
		(prev, cur) => prev + cur.points,
		0,
	);

	useEffect(function () {
		fetch('/questions.json')
			.then((res) => res.json())
			.then((data) => {
				const allQuestions = data.questions;
				setTotalQuestions(allQuestions.length);

				const shuffledQuestions = [...data.questions];
				shuffleArray(shuffledQuestions);
				const randomQuestions = shuffledQuestions.slice(0, 15);
				dispatch({ type: 'dataReceived', payload: randomQuestions });
			})
			.catch((err) => dispatch({ type: 'dataFailed' }));
	}, []);

	return (
		<QuizContext.Provider
			value={{
				questions,
				status,
				index,
				answer,
				points,
				highscore,
				secondsRemaining,
				maxPossiblePoints,
				totalQuestions,
				numQuestions: questions.length,

				dispatch,
			}}
		>
			{children}
		</QuizContext.Provider>
	);
}

function useQuiz() {
	const context = useContext(QuizContext);
	if (context === undefined) {
		throw new Error('useQuiz must be used within a QuizProvider');
	}
	return context;
}

export { QuizProvider, useQuiz };
